// Card Pricing Management System for CardsPay
import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';

class CardPricingManager {
  constructor() {
    this.defaultPricing = null;
    this.customerOverrides = {};
    this.listeners = [];
  }

  // Initialize and listen to pricing changes
  async initialize() {
    const pricingDoc = doc(db, 'cardPricingSettings', 'cardspay-config');

    // Set up real-time listener
    const unsubscribe = onSnapshot(pricingDoc, async (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        this.defaultPricing = data.default || this.getDefaultStructure();

        // Process customer overrides - handle both underscore and dot formats
        const rawOverrides = data.customerOverrides || {};
        this.customerOverrides = {};
        let needsCleanup = false;
        const cleanedOverrides = {};

        Object.entries(rawOverrides).forEach(([key, value]) => {
          // Check if value has an email property
          if (value && value.email) {
            this.customerOverrides[value.email] = value;
            cleanedOverrides[key] = value;
          }
          // Handle underscore format
          else if (key.includes('_') && key.includes('@')) {
            const email = key.replace(/_/g, '.');
            this.customerOverrides[email] = { ...value, email };
            cleanedOverrides[key] = { ...value, email };
          }
          // Handle malformed keys
          else if (key.includes('@') && !key.includes('.') && !key.includes('_')) {
            needsCleanup = true;
          }
          // Normal email format
          else if (key.includes('@') && key.includes('.')) {
            const firebaseKey = key.replace(/\./g, '_');
            this.customerOverrides[key] = { ...value, email: key };
            cleanedOverrides[firebaseKey] = { ...value, email: key };
            needsCleanup = true;
          }
          else {
            cleanedOverrides[key] = value;
          }
        });

        // Auto-cleanup if needed
        if (needsCleanup) {
          try {
            await setDoc(doc.ref, {
              ...data,
              customerOverrides: cleanedOverrides
            });
          } catch (error) {
          }
        }

        this.notifyListeners();
      } else {
        // Create default structure if doesn't exist
        const defaultStructure = this.getDefaultStructure();
        await setDoc(pricingDoc, {
          default: defaultStructure,
          customerOverrides: {},
          lastUpdated: new Date().toISOString(),
          updatedBy: 'system'
        });
        this.defaultPricing = defaultStructure;
        this.notifyListeners();
      }
    });

    return unsubscribe;
  }

  // Default pricing structure
  getDefaultStructure() {
    return {
      // Default price per BIN
      binPrices: {
        '525847': 10,  // Digital Wallet Card
        '559347': 15,  // Premium Card
        '483312': 10,  // Standard Card
      },
      defaultPrice: 10,  // Fallback price if BIN not specified
      lastUpdated: new Date().toISOString(),
      updatedBy: 'system'
    };
  }

  // Get default card price (no customer override)
  getDefaultCardPrice(bin) {
    const binPrices = this.defaultPricing?.binPrices || {};
    const defaultPrice = this.defaultPricing?.defaultPrice || 10;

    const price = binPrices[bin] !== undefined ? binPrices[bin] : defaultPrice;

    return {
      price,
      customPrice: false,
      source: binPrices[bin] !== undefined ? 'system-bin' : 'system-default'
    };
  }

  // Get price for a specific customer and BIN
  getCardPrice(customerEmail, bin) {
      const override = this.customerOverrides[customerEmail];
      if (!override) {
        return this.getDefaultCardPrice(bin);
      }

      // Check if customer has a default price override
      if (override.defaultPrice !== undefined) {
        return {
          price: override.defaultPrice,
          customPrice: true,
          source: 'customer-default-override'
        };
      }

    // Use system default pricing
    const binPrices = this.defaultPricing?.binPrices || {};
    const defaultPrice = this.defaultPricing?.defaultPrice || 10;

    const price = binPrices[bin] !== undefined ? binPrices[bin] : defaultPrice;


    return {
      price,
      customPrice: false,
      source: binPrices[bin] !== undefined ? 'system-bin' : 'system-default'
    };
  }

  // Update default pricing
  async updateDefaultPricing(binPrices, defaultPrice, updatedBy = 'admin') {
    const pricingDoc = doc(db, 'cardPricingSettings', 'cardspay-config');

    try {
      await updateDoc(pricingDoc, {
        'default.binPrices': binPrices,
        'default.defaultPrice': defaultPrice,
        'default.lastUpdated': new Date().toISOString(),
        'default.updatedBy': updatedBy
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Set customer-specific pricing
  async setCustomerPricing(customerEmail, pricing, updatedBy = 'admin') {
    const pricingDoc = doc(db, 'cardPricingSettings', 'cardspay-config');
    const firebaseKey = customerEmail.replace(/\./g, '_');

    try {
      const customerPricing = {
        email: customerEmail,
        binPrices: pricing.binPrices || {},
        defaultPrice: pricing.defaultPrice,
        note: pricing.note || '',
        lastUpdated: new Date().toISOString(),
        updatedBy
      };

      await updateDoc(pricingDoc, {
        [`customerOverrides.${firebaseKey}`]: customerPricing
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Remove customer-specific pricing
  async removeCustomerPricing(customerEmail) {
    const pricingDoc = doc(db, 'cardPricingSettings', 'cardspay-config');
    const firebaseKey = customerEmail.replace(/\./g, '_');

    try {
      const docSnap = await getDoc(pricingDoc);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const overrides = data.customerOverrides || {};
        delete overrides[firebaseKey];

        await updateDoc(pricingDoc, {
          customerOverrides: overrides
        });
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Get all customer overrides
  getAllCustomerOverrides() {
    return Object.values(this.customerOverrides);
  }

  // Subscribe to pricing changes
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  notifyListeners() {
    this.listeners.forEach(callback => callback());
  }
}

// Export singleton instance
const cardPricingManager = new CardPricingManager();
export default cardPricingManager;
